import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority"
import { cn } from "@/lib/utils"

const buttonVariants = cva(
  [
    "relative isolate inline-flex items-center justify-center",
    "gap-2 whitespace-nowrap rounded-full",
    "text-sm font-medium",
    "transition-all duration-300 ease-out",
    "focus-visible:outline-none",
    "focus-visible:ring-2 focus-visible:ring-primary/25",
    "focus-visible:ring-offset-2",
    "disabled:pointer-events-none disabled:opacity-50",
    "active:scale-[0.98]",
    "hover:-translate-y-[1px]",
    "overflow-hidden",

    // Icon behavior
    "[&_svg]:pointer-events-none",
    "[&_svg]:size-4",
    "[&_svg]:shrink-0",

    // Glass highlight
    "before:absolute before:inset-0 before:-z-10",
    "before:rounded-[inherit]",
    "before:pointer-events-none",
    "before:bg-[radial-gradient(circle_at_20%_0%,rgba(255,255,255,0.75),transparent_38%),linear-gradient(90deg,rgba(255,255,255,0.22),transparent_45%,rgba(255,255,255,0.12))]",

    // Inner glass edge
    "after:absolute after:inset-[1px] after:-z-10",
    "after:rounded-[inherit]",
    "after:pointer-events-none",
    "after:border after:border-white/20",
  ].join(" "),
  {
    variants: {
      variant: {
        default: [
          // Main HeDesk CTA
          "text-white",
          "border border-white/30",
          "bg-primary/85",
          "backdrop-blur-2xl",
          "[webkit-backdrop-filter:blur(24px)_saturate(180%)]",
          "shadow-[inset_0_1px_0_rgba(255,255,255,0.35),inset_0_-1px_0_rgba(0,0,0,0.08),0_10px_30px_rgba(23,105,232,0.16)]",
          "hover:bg-primary",
          "hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.45),0_14px_40px_rgba(23,105,232,0.22)]",
        ].join(" "),

        destructive: [
          "text-white",
          "border border-white/25",
          "bg-red-500/85",
          "backdrop-blur-2xl",
          "shadow-[inset_0_1px_0_rgba(255,255,255,0.3),0_8px_24px_rgba(239,68,68,0.15)]",
          "hover:bg-red-500",
        ].join(" "),

        outline: [
          "text-primary",
          "border border-primary/15",
          "bg-white/45",
          "backdrop-blur-2xl",
          "[webkit-backdrop-filter:blur(24px)_saturate(160%)]",
          "shadow-[inset_0_1px_0_rgba(255,255,255,0.8),0_8px_25px_rgba(23,105,232,0.08)]",
          "hover:bg-white/65",
          "hover:border-primary/25",
          "hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.9),0_12px_32px_rgba(23,105,232,0.12)]",
        ].join(" "),

        secondary: [
          "text-primary",
          "border border-white/60",
          "bg-blue-50/55",
          "backdrop-blur-2xl",
          "[webkit-backdrop-filter:blur(24px)_saturate(160%)]",
          "shadow-[inset_0_1px_0_rgba(255,255,255,0.8),0_6px_22px_rgba(23,105,232,0.07)]",
          "hover:bg-blue-50/80",
        ].join(" "),

        ghost: [
          "text-foreground",
          "border border-transparent",
          "bg-transparent",
          "before:hidden",
          "after:hidden",
          "hover:bg-white/45",
          "hover:backdrop-blur-xl",
        ].join(" "),

        link: [
          "text-primary",
          "border-transparent",
          "bg-transparent",
          "before:hidden",
          "after:hidden",
          "hover:underline",
          "hover:translate-y-0",
        ].join(" "),
      },

      size: {
        default: "h-10 px-5",
        sm: "h-9 px-4 text-xs",
        lg: "h-12 px-7 text-sm",
        icon: "h-10 w-10",
      },
    },

    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button"

    return (
      <Comp
        ref={ref}
        className={cn(
          buttonVariants({
            variant,
            size,
            className,
          })
        )}
        {...props}
      />
    )
  }
)

Button.displayName = "Button"

export { Button, buttonVariants }